import { BaseWebComponentDataProvider } from '../../framework/BaseWebComponentDataProvider';
import { GridViewSettings } from '../../grid/utils/types';
import { Column, ColumnKeys } from '../types';
import { ColumnDetails, ColumnType } from '../../grid/types/common';
import { ViewConfiguration, ViewType } from '../../grid/gridView/types';
import { Filter } from '../../grid/gridFilters/types';
import {
  convertTranslationToGridEnumValues,
  createCategoryTranslations,
  createSensorTranslations,
} from '../eventMappings/Translations';

export class RawEventsConfigurationGridViewSettingsDataProvider extends BaseWebComponentDataProvider {
  private sensorEnumValues: Array<{ value: string; label: string }>;
  private eventTypeEnumValues: Array<{ value: string; label: string }>;
  private categoryEnumValues: Array<{ value: string; label: string }>;

  constructor(dependencies: any) {
    super();
    this.injectDependencies(dependencies);

    this.sensorEnumValues = convertTranslationToGridEnumValues(createSensorTranslations(this.dependencies.translateFn));

    this.categoryEnumValues = convertTranslationToGridEnumValues(
      createCategoryTranslations(this.dependencies.translateFn)
    );
  }

  getAllColumns(): Array<ColumnDetails<Column, ColumnKeys>> {
    const commonDefaults = {
      removable: false,
      required: true,
      editingDisabled: true,
    };

    const columns: Array<Omit<ColumnDetails<Column, ColumnKeys>, 'removable'>> = [
      {
        column: Column.EVENT_TYPE,
        key: ColumnKeys.EVENT_TYPE,
        columnName: this.dependencies.translateFn('Event type'),
        type: ColumnType.STRING,
      },
      {
        column: Column.CATEGORY,
        key: ColumnKeys.CATEGORY,
        columnName: this.dependencies.translateFn('Category'),
        type: ColumnType.ENUM,
        values: this.categoryEnumValues,
      },
      {
        column: Column.SENSOR_TYPE,
        key: ColumnKeys.SENSOR_TYPE,
        columnName: this.dependencies.translateFn('Sensor type'),
        type: ColumnType.ENUM,
        values: this.sensorEnumValues,
      },
      {
        column: Column.OS_TYPES,
        key: ColumnKeys.OS_TYPES,
        columnName: this.dependencies.translateFn('OS type'),
        // It is not actually an action, but there is no appropriate type for it
        type: ColumnType.CUSTOM,
      },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName: this.dependencies.translateFn('Status'),
        type: ColumnType.ENUM,
        values: [
          {
            value: 'filtered',
            label: this.dependencies.translateFn('Enabled'),
          },
          {
            value: 'disabled',
            label: this.dependencies.translateFn('Disabled'),
          },
        ],
      },
      {
        column: Column.REQUIREMENTS,
        key: ColumnKeys.REQUIREMENTS,
        columnName: this.dependencies.translateFn('Requirements'),
        type: ColumnType.STRING,
      },
    ];

    if (this.dependencies.isPartnerCompany()) {
      columns.unshift({
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: this.dependencies.translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        sortable: false,
        filterResetValue: this.getCompanyDefaultFilterValue(),
        isHiddenInGridSettings: true,
      });
    }

    return columns.map(colDetails => ({ ...colDetails, ...commonDefaults }));
  }

  getFilters(): Array<Filter<ColumnKeys>> {
    const filters: Array<Filter<ColumnKeys>> = [
      { [ColumnKeys.EVENT_TYPE]: { contains: '' } },
      { [ColumnKeys.CATEGORY]: { in: [] } },
      { [ColumnKeys.SENSOR_TYPE]: { in: [] } },
      { [ColumnKeys.STATUS]: { in: [] } },
    ];

    if (this.dependencies.isPartnerCompany()) {
      filters.unshift({ [ColumnKeys.COMPANY]: this.getCompanyDefaultFilterValue() });
    }

    return filters;
  }

  private getCompanyDefaultFilterValue() {
    return { exactMatch: this.dependencies.getCompanyId() };
  }

  getView(): ViewConfiguration<Column, ColumnKeys> {
    return {
      id: 'Default',
      name: 'Default',
      type: ViewType.DEFAULT,
      filters: this.getFilters(),
      displayedColumns: [
        Column.EVENT_TYPE,
        Column.CATEGORY,
        Column.SENSOR_TYPE,
        Column.OS_TYPES,
        Column.STATUS,
        Column.REQUIREMENTS,
      ],
      compactView: false,
      pageSize: 500, // Should be the same as the elements limit on a grid without pagination
    };
  }

  async getDataForView(): Promise<GridViewSettings<Column, ColumnKeys>> {
    const columnKeys: ColumnKeys[] = [
      ColumnKeys.EVENT_TYPE,
      ColumnKeys.CATEGORY,
      ColumnKeys.SENSOR_TYPE,
      ColumnKeys.STATUS,
    ];

    if (this.dependencies.isPartnerCompany()) {
      columnKeys.unshift(ColumnKeys.COMPANY);
    }

    return {
      allColumns: this.getAllColumns(),
      defaultViewProps: this.getView(),
      views: [this.getView()],
      predefinedFilterKeys: columnKeys,
    };
  }
}
